import { BotSequenceType } from "./BotSequence.Enums";
import { BotSequenceConfigStep } from "./BotSequenceConfig.Interfaces";

// LINK apps/bot/src/API/Sequences/Sequences.Controller.ts
// LINK apps/bull/src/Services/BotSequences/Handler/BotSequenceHandler.Service.ts

/**
 * BotSequenceType → bot opening-prompt slug (BotPromptDefaults DEFAULT_SEQUENCES).
 * Every BotSequenceType must map to a registered prompt slug with an `sms` variant —
 * guarded by SequenceFire.Helpers.spec.ts so a new type can never fire promptless.
 */
export const BOT_SEQUENCE_PROMPT_SLUG: Record<BotSequenceType, string> = {
  [BotSequenceType.PAST_DUE]: "sequence-past-due",
  [BotSequenceType.ABANDONED_CART]: "sequence-abandoned-cart",
  [BotSequenceType.MOVE_IN]: "sequence-move-in",
  [BotSequenceType.MOVE_OUT]: "sequence-move-out"
};

/**
 * Identifiers and dates ONLY — never frozen anchor amounts (`balance`,
 * `paidThroughDate`, …). The enrollment anchor is audit evidence, explicitly not
 * render context (reconciler-implementation-design.md §2.1b): the message must
 * derive its facts from the just-JIT-refreshed DB rows on the bot side, which the
 * system prompt already injects fresh.
 */
export interface BotSequenceFireContext {
  /** Day 0 of the enrollment (ISO date) — lets the model phrase "N days" correctly. */
  anchorDate?: string;
  /** The attempt's location-local day (ISO date) — `{{Sequence:Date}}` and the FMS note. */
  localDay?: string;
}

/**
 * The resolved day the handler fires (ENG-3216): what to deliver and on which channels,
 * plus the per-day actions. Taken from the fresh config at fire time, never from the
 * booking, so a mid-day edit wins.
 */
export type BotSequenceFireStep = Pick<BotSequenceConfigStep, "offsetDays" | "deliveries" | "email" | "fmsNote" | "deferrable">;

export interface BotSequenceFireMessageRequest {
  orgId: number;
  userId: number;
  /** Enrollment location — resolves the from-number and the prompt focus. */
  locationId?: number;
  sequenceType: BotSequenceType;
  /** Ladder day offset this fire represents — context + logging. */
  stepOffset: number;
  /**
   * The day's settings (ENG-3216). Absent (Studio/test callers, or a pre-ENG-3216 caller)
   * ⇒ one generated SMS delivery.
   */
  step?: BotSequenceFireStep;
  /** Enrollment-scoped ids the variable renderer must use instead of the user's "current" ones (multi-unit tenants). */
  unitId?: number;
  tenantId?: number;
  /**
   * The location's LIVE FMS integration row id, read by the bull handler from the location world facts: the integration-id
   * variable of the store the org's `fms_sync_cortex_vars` flag selects (`FMSIntegrationId` or `CortexFacilityFMSIntegrationId`),
   * inheritance applied; never the enrollment's stored snapshot (ENG-3415 C-11). The FMS note writer needs its type and token.
   */
  fmsId?: number;
  /**
   * The location's LIVE FMS-side facility id, read by the bull handler from the location world facts: the facility-id variable
   * of the store the org's `fms_sync_cortex_vars` flag selects (`FMSId` or `CortexFacilityFMSId`), inheritance applied; never
   * the enrollment's stored snapshot (ENG-3415 C-11). The note write-back uses it as `facilityId`.
   */
  externalLocationId?: string;
  /** @deprecated pre-ENG-3216 shape; ignored when `step` is present. */
  channel?: "sms";
  /** Written to the attempt row by the bot-side finalize; required for the async fire path. */
  enrollmentId?: number;
  /**
   * The idempotency key: the bot refuses to re-generate when a SEQUENCE_TRIGGER event
   * already carries this attemptId, so a retried POST (Heroku H12 → client 503-retry,
   * network re-POST) can never double-send. Absent ⇒ no dedup (Studio/test callers).
   */
  attemptId?: number;
  /**
   * Outbound Gate plan decision 1: the location's mode is SIMULATED — build every delivery in
   * full, record it, and let none leave (each is `simulated (sequence_simulated)`).
   */
  simulate?: boolean;
  /** Which lease this fire concerns (PAST_DUE / move types). */
  leaseId?: number;
  /** Which cart lead this fire concerns (ABANDONED_CART — the lead IS the cart). */
  leadId?: number;
  context?: BotSequenceFireContext;
  /**
   * ENG-3511 D-2: ISO-8601 absolute timestamp at or after which this fire must not send — the end of the allowed send
   * interval containing the send time (exclusive, like the interval itself), capped at `sendingAt` + the stale-`sending`
   * sweep horizon. Set by the bull handler. The bot processor checks it once, when the fire job starts, and cancels with
   * `outside_send_window` once `now` has reached it. Absent (an older bull during a rolling deploy) ⇒ no check.
   */
  fireDeadline?: string;
}

/**
 * What a sequence voice call carries in its Redis call metadata (ENG-3216 decisions 13/34/35):
 * the fire request (so the gateway and the status webhook can write the ledger and complete
 * the attempt), the opener, and the pre-rendered voicemail. Rendered BEFORE the call is placed
 * — a call whose text cannot render is never dialed.
 */
export interface SequenceVoiceFireContext {
  params: BotSequenceFireMessageRequest;
  opener: { mode: "verbatim"; text: string } | { mode: "generated"; guidance?: string };
  /** Exact wording spoken after the beep; null = hang up on a machine without leaving anything. */
  voicemailText: string | null;
  /** The why-context persisted as the priming record and fed to the LLM in generated mode. */
  contextText: string;
}

/**
 * Business outcomes always ride an HTTP 200 — the API client throws on non-2xx, so
 * only auth/infra failures surface as throws. `skipped` is separated from `error`
 * so the caller can distinguish "retry another day" from "send-shaped failure".
 *
 * `accepted` is the async happy path (the 202-flow): the cheap guards passed and
 * generation+send continue in the background — the response returns in ~1s, far
 * under Heroku's 30s router cutoff (H12 → 503 → the client's single POST retry
 * used to systematically double-send on slow generations). The BOT then finalizes
 * the attempt itself (`sending → fired/cancelled/failed`); the bull caller logs
 * and exits. Definitive guard rejections (`blocked` / `skipped` / cheap
 * `error`s) still return synchronously and are finalized by bull as before.
 * `simulated` / `blocked` are the Outbound Gate's answers (ENG-3373): withheld at the wire /
 * the person opted out.
 */
export type BotSequenceFireMessageStatus = "accepted" | "sent" | "simulated" | "blocked" | "skipped" | "error";

export interface BotSequenceFireMessageResult {
  status: BotSequenceFireMessageStatus;
  conversationId?: number;
  /** BotMessage DB id of the generated assistant turn. */
  messageId?: number;
  /** Twilio message SID when status === "sent". */
  externalSid?: string;
  /**
   * Machine-readable reason: "no_phone_on_user" | "sms_not_configured" |
   * "conversation_escalated" | "empty_generation" | "unsupported_channel" |
   * "internal_error" | channel error text.
   */
  error?: string;
}
